/**
 * Full-page answer when the API refuses the workspace itself (403
 * TENANT_SUSPENDED / TENANT_NOT_ACTIVE from the session gate).
 *
 * Without it every widget on the page failed on its own with the same 403,
 * which reads as a broken product rather than a workspace someone has to
 * reactivate. Sign-out still works here: POST /v1/auth/logout is the one route
 * an inactive workspace may reach.
 *
 * The wording follows the workspace's status (D15): an owner whose workspace
 * is still being set up is told to wait and check again, not to contact an
 * administrator who is, in that case, themselves.
 */
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Button, Card } from "@/components/ui";
import { Icon } from "@/components/icons";
import { WORKSPACE_BLOCK_KEYS } from "@/lib/auth/messages";
import { signOut } from "@/lib/auth/sign-out";
import { useAuthStore } from "@/lib/auth/store";
import { useTranslation } from "@/lib/i18n";
import {
  checkWorkspaceAgain,
  workspaceBlockKind,
} from "@/lib/auth/workspace-block";

export function WorkspaceUnavailable() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const block = useAuthStore((s) => s.workspaceBlock);
  const [busy, setBusy] = useState<"check" | "signOut" | null>(null);
  const [stillBlocked, setStillBlocked] = useState(false);

  const keys =
    WORKSPACE_BLOCK_KEYS[block ? workspaceBlockKind(block) : "notActive"];
  const copy = { title: t(keys.title), body: t(keys.body) };

  const onCheckAgain = async () => {
    setBusy("check");
    setStillBlocked(false);
    // On success the block is cleared and this page is replaced by the app.
    if (!(await checkWorkspaceAgain(queryClient))) {
      setStillBlocked(true);
      setBusy(null);
    }
  };

  const onSignOut = async () => {
    setBusy("signOut");
    await signOut();
    navigate("/login", { replace: true });
  };

  return (
    <main
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div style={{ inlineSize: "100%", maxInlineSize: 440 }}>
        <Card>
          <div role="alert">
            <h1 style={{ marginBlock: "0 8px", fontSize: 20 }}>{copy.title}</h1>
            <p style={{ marginBlock: "0 16px" }}>{copy.body}</p>
          </div>
          <p role="status" style={{ marginBlock: "0 16px" }}>
            {stillBlocked ? t("session.stillUnavailable") : null}
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            <Button
              variant="primary"
              disabled={busy !== null}
              onClick={() => void onCheckAgain()}
            >
              <Icon name="refresh" size={16} />{" "}
              {busy === "check"
                ? t("session.checking")
                : t("session.checkAgain")}
            </Button>
            <Button
              variant="secondary"
              disabled={busy !== null}
              onClick={() => void onSignOut()}
            >
              <Icon name="logout" size={16} />{" "}
              {busy === "signOut"
                ? t("session.signingOut")
                : t("session.signOut")}
            </Button>
          </div>
          {block && (
            // For support: the catalog keeps `detail` (English, written for
            // developers) out of the message and in a Details area.
            <details style={{ marginBlockStart: 16, fontSize: 13 }}>
              <summary>{t("common.details")}</summary>
              <p style={{ marginBlock: "8px 0" }}>
                <code>{block.code}</code> {block.detail}
              </p>
            </details>
          )}
        </Card>
      </div>
    </main>
  );
}
